// src/services/companyMasterDataService.js
// =============================================================================
// Frontend API wrapper for Company Master Data
// =============================================================================
//
// PURPOSE
// -------
// This service connects the new Master Data frontend area to the backend routes
// introduced in backend batch 48:
//
//   GET    /master-data/company
//   PUT    /master-data/company
//   GET    /master-data/company/addresses
//   POST   /master-data/company/addresses
//   PUT    /master-data/company/addresses/:id
//   DELETE /master-data/company/addresses/:id
//   GET    /master-data/company/bank-accounts
//   POST   /master-data/company/bank-accounts
//   PUT    /master-data/company/bank-accounts/:id
//   DELETE /master-data/company/bank-accounts/:id
//
// ARCHITECTURAL BOUNDARY
// ----------------------
// React pages should not know backend URL details, Axios error shapes, or the
// details of apiClient identity/session headers. This file follows the same
// hb_visits pattern as salesService.js and stockService.js:
// - use the central apiClient,
// - normalize API errors into regular Error objects,
// - keep request payload construction explicit,
// - leave business validation and write authorization to the backend.
//
// IMPORTANT SNAPSHOT RULE
// -----------------------
// Company Master Data stores CURRENT seller/company data only. Future invoice
// generation should copy seller data into sales document snapshots when a
// document is posted/issued. The frontend may edit current master data here,
// but it should not try to solve historic invoice snapshots.

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

/* -------------------------------------------------------------------------- */
/* Stable frontend constants                                                  */
/* -------------------------------------------------------------------------- */

export const COMPANY_ADDRESS_TYPES = Object.freeze({
  REGISTERED: "registered",
  OPERATING: "operating",
  CORRESPONDENCE: "correspondence",
  INVOICING: "invoicing",
  OTHER: "other",
});

export const VAT_PAYER_STATUSES = Object.freeze({
  UNKNOWN: "unknown",
  VAT_PAYER: "vat_payer",
  VAT_EXEMPT: "vat_exempt",
  SMALL_BUSINESS_EXEMPTION: "small_business_exemption",
});

export const KSEF_ENVIRONMENTS = Object.freeze({
  TEST: "test",
  DEMO: "demo",
  PRODUCTION: "production",
});

/* -------------------------------------------------------------------------- */
/* Error normalization                                                        */
/* -------------------------------------------------------------------------- */

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Convert an Axios error into a regular Error while preserving useful metadata.
 * Page components can display error.message and still inspect e.status/e.code
 * later if field-level validation messages are added.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;
  e.code = err?.response?.data?.code || err?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[CompanyMasterDataService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

/* -------------------------------------------------------------------------- */
/* Small payload helpers                                                      */
/* -------------------------------------------------------------------------- */

function isBlank(value) {
  return value === undefined || value === null || `${value}`.trim() === "";
}

function assertId(context, id) {
  if (isBlank(id)) {
    const e = new Error(`${context}: id is required.`);
    e.code = "COMPANY_MASTER_DATA_ID_MISSING";
    throw e;
  }
}

function assertPayload(context, payload) {
  if (!payload || typeof payload !== "object") {
    const e = new Error(`${context}: payload object is required.`);
    e.code = "COMPANY_MASTER_DATA_PAYLOAD_MISSING";
    throw e;
  }
}

function cleanText(value) {
  return String(value ?? "").trim();
}

function cleanNullableText(value) {
  const text = cleanText(value);
  return text || null;
}

function cleanUpper(value, fallback = "") {
  const text = cleanText(value || fallback).toUpperCase();
  return text || fallback;
}

function cleanNumberLike(value) {
  if (value === undefined || value === null || value === "") return null;
  return value;
}

function cleanBool(value) {
  return value === true;
}

/**
 * Build the payload for PUT /master-data/company.
 *
 * The backend accepts both snake_case and camelCase, but using backend field
 * names here makes the API contract obvious and avoids accidental naming drift.
 */
function normalizeCompanyProfilePayload(payload = {}) {
  return {
    // General tab -------------------------------------------------------------
    legal_name: cleanNullableText(payload.legal_name),
    trade_name: cleanNullableText(payload.trade_name),
    tax_id: cleanNullableText(payload.tax_id),
    regon: cleanNullableText(payload.regon),
    krs: cleanNullableText(payload.krs),
    country_code: cleanUpper(payload.country_code, "PL").slice(0, 2),
    default_currency: cleanUpper(payload.default_currency, "PLN").slice(0, 3),
    default_language: cleanText(payload.default_language || "pl"),
    vat_payer_status: cleanText(payload.vat_payer_status || VAT_PAYER_STATUSES.UNKNOWN),

    // Contact tab -------------------------------------------------------------
    public_email: cleanNullableText(payload.public_email),
    invoice_email: cleanNullableText(payload.invoice_email),
    phone: cleanNullableText(payload.phone),
    website: cleanNullableText(payload.website),

    // Invoicing tab -----------------------------------------------------------
    default_place_of_issue: cleanNullableText(payload.default_place_of_issue),
    default_payment_term_days: cleanNumberLike(payload.default_payment_term_days) ?? 0,
    default_payment_method_code: cleanNullableText(payload.default_payment_method_code),
    invoice_footer_text: cleanNullableText(payload.invoice_footer_text),
    receipt_footer_text: cleanNullableText(payload.receipt_footer_text),
    invoice_notes: cleanNullableText(payload.invoice_notes),

    // Print / Branding tab ----------------------------------------------------
    logo_media_file_id: cleanNumberLike(payload.logo_media_file_id),
    small_logo_media_file_id: cleanNumberLike(payload.small_logo_media_file_id),
    signature_media_file_id: cleanNumberLike(payload.signature_media_file_id),
    print_accent_style: cleanText(payload.print_accent_style || "default"),
    invoice_header_text: cleanNullableText(payload.invoice_header_text),

    // Integrations / Future tab ----------------------------------------------
    ksef_enabled: cleanBool(payload.ksef_enabled),
    ksef_environment: cleanNullableText(payload.ksef_environment),
    fiscal_printer_enabled: cleanBool(payload.fiscal_printer_enabled),
    external_accounting_system_id: cleanNullableText(payload.external_accounting_system_id),
    integration_notes: cleanNullableText(payload.integration_notes),
  };
}

function normalizeAddressPayload(payload = {}) {
  return {
    address_type: cleanText(payload.address_type || COMPANY_ADDRESS_TYPES.REGISTERED),
    label: cleanNullableText(payload.label),
    recipient_name: cleanNullableText(payload.recipient_name),
    address_line1: cleanNullableText(payload.address_line1),
    address_line2: cleanNullableText(payload.address_line2),
    postal_code: cleanNullableText(payload.postal_code),
    city: cleanNullableText(payload.city),
    region: cleanNullableText(payload.region),
    country_code: cleanUpper(payload.country_code, "PL").slice(0, 2),
    is_default: cleanBool(payload.is_default),
    is_active: payload.is_active !== false,
    sort_order: cleanNumberLike(payload.sort_order) ?? 0,
    comment: cleanNullableText(payload.comment),
  };
}

function normalizeBankAccountPayload(payload = {}) {
  return {
    account_label: cleanNullableText(payload.account_label),
    bank_name: cleanNullableText(payload.bank_name),
    account_number: cleanNullableText(payload.account_number),
    iban: cleanNullableText(payload.iban),
    swift_bic: cleanNullableText(payload.swift_bic),
    currency: cleanUpper(payload.currency, "PLN").slice(0, 3),
    is_default: cleanBool(payload.is_default),
    is_active: payload.is_active !== false,
    sort_order: cleanNumberLike(payload.sort_order) ?? 0,
    comment: cleanNullableText(payload.comment),
  };
}

/* -------------------------------------------------------------------------- */
/* API functions                                                              */
/* -------------------------------------------------------------------------- */

export const fetchCompanyMasterData = async (params = {}) => {
  try {
    const res = await apiClient.get("/master-data/company", {
      withCredentials: true,
      params,
    });

    return {
      company: res?.data?.company || {},
      addresses: Array.isArray(res?.data?.addresses) ? res.data.addresses : [],
      bankAccounts: Array.isArray(res?.data?.bankAccounts) ? res.data.bankAccounts : [],
    };
  } catch (err) {
    throwApiError("fetchCompanyMasterData", err);
  }
};

export const updateCompanyMasterData = async (payload = {}) => {
  try {
    assertPayload("updateCompanyMasterData", payload);

    const res = await apiClient.put(
      "/master-data/company",
      normalizeCompanyProfilePayload(payload),
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return {
      company: res?.data?.company || {},
      addresses: Array.isArray(res?.data?.addresses) ? res.data.addresses : [],
      bankAccounts: Array.isArray(res?.data?.bankAccounts) ? res.data.bankAccounts : [],
    };
  } catch (err) {
    throwApiError("updateCompanyMasterData", err);
  }
};

export const fetchCompanyAddresses = async (params = {}) => {
  try {
    const res = await apiClient.get("/master-data/company/addresses", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchCompanyAddresses", err);
  }
};

export const createCompanyAddress = async (payload = {}) => {
  try {
    assertPayload("createCompanyAddress", payload);

    const res = await apiClient.post(
      "/master-data/company/addresses",
      normalizeAddressPayload(payload),
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("createCompanyAddress", err);
  }
};

export const updateCompanyAddress = async (id, payload = {}) => {
  try {
    assertId("updateCompanyAddress", id);
    assertPayload("updateCompanyAddress", payload);

    const res = await apiClient.put(
      `/master-data/company/addresses/${encodeURIComponent(id)}`,
      normalizeAddressPayload(payload),
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("updateCompanyAddress", err);
  }
};

export const deleteCompanyAddress = async (id) => {
  try {
    assertId("deleteCompanyAddress", id);

    const res = await apiClient.delete(
      `/master-data/company/addresses/${encodeURIComponent(id)}`,
      { withCredentials: true }
    );

    return res?.data;
  } catch (err) {
    throwApiError("deleteCompanyAddress", err);
  }
};

export const fetchCompanyBankAccounts = async (params = {}) => {
  try {
    const res = await apiClient.get("/master-data/company/bank-accounts", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("fetchCompanyBankAccounts", err);
  }
};

export const createCompanyBankAccount = async (payload = {}) => {
  try {
    assertPayload("createCompanyBankAccount", payload);

    const res = await apiClient.post(
      "/master-data/company/bank-accounts",
      normalizeBankAccountPayload(payload),
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("createCompanyBankAccount", err);
  }
};

export const updateCompanyBankAccount = async (id, payload = {}) => {
  try {
    assertId("updateCompanyBankAccount", id);
    assertPayload("updateCompanyBankAccount", payload);

    const res = await apiClient.put(
      `/master-data/company/bank-accounts/${encodeURIComponent(id)}`,
      normalizeBankAccountPayload(payload),
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("updateCompanyBankAccount", err);
  }
};

export const deleteCompanyBankAccount = async (id) => {
  try {
    assertId("deleteCompanyBankAccount", id);

    const res = await apiClient.delete(
      `/master-data/company/bank-accounts/${encodeURIComponent(id)}`,
      { withCredentials: true }
    );

    return res?.data;
  } catch (err) {
    throwApiError("deleteCompanyBankAccount", err);
  }
};
